import React, { useState, useMemo } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { PermitLetter, LetterGeneration, Car } from '../../types';
import CarLetterForm from '../../components/HR/CarLetterForm';
import CarLetterGenerator from '../../components/HR/CarLetterGenerator';
import { useCars } from '../../hooks/useCars';
import { usePermitLetters, useCreatePermitLetter, useUpdatePermitLetter, useDeletePermitLetter } from '../../hooks/usePermitLetters';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';

const CarLetters: React.FC = () => {
  const { data: apiCars = [] } = useCars({ populate: '*' });
  const cars = apiCars as Car[];

  const queryOptions = useMemo(() => ({ populate: '*' }), []);
  const { data: allPermitLetters = [], isLoading, error, refetch } = usePermitLetters(queryOptions);
  const letters = useMemo(() => allPermitLetters.filter(l => l.type === 'car'), [allPermitLetters]);

  const createMutation = useCreatePermitLetter(() => refetch());
  const updateMutation = useUpdatePermitLetter(() => refetch());
  const deleteMutation = useDeletePermitLetter(() => refetch());

  const [generations, setGenerations] = useState<LetterGeneration[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState<PermitLetter | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'templates' | 'generations'>('templates');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createdByFilter, setCreatedByFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const uniqueCreatedBy = Array.from(new Set(letters.map(letter => letter.createdBy).filter(Boolean)));
  const uniqueGeneratedBy = Array.from(new Set(generations.map(gen => gen.generatedBy).filter(Boolean)));

  const filteredLetters = useMemo(() => {
    return letters.filter(letter => {
      const matchesSearch = !searchTerm || (
        letter.templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        letter.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        letter.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        letter.createdBy.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesType = typeFilter === 'all' ||
        (typeFilter === 'default' && letter.isDefault) ||
        (typeFilter === 'custom' && !letter.isDefault);
      const matchesCreatedBy = createdByFilter === 'all' || letter.createdBy === createdByFilter;
      const createdDate = new Date(letter.createdAt);
      const matchesDateFrom = !dateFromFilter || createdDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || createdDate <= new Date(dateToFilter);
      return matchesSearch && matchesType && matchesCreatedBy && matchesDateFrom && matchesDateTo;
    });
  }, [letters, searchTerm, typeFilter, createdByFilter, dateFromFilter, dateToFilter]);

  const filteredGenerations = useMemo(() => {
    return generations.filter(generation => {
      const letter = letters.find(l => l.id === generation.letterId);
      const matchesSearch = !searchTerm || (
        (letter?.templateName && letter.templateName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (generation.notes && generation.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
        generation.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        generation.generatedBy.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || generation.status === statusFilter;
      const matchesGeneratedBy = createdByFilter === 'all' || generation.generatedBy === createdByFilter;
      const generatedDate = new Date(generation.generatedDate);
      const matchesDateFrom = !dateFromFilter || generatedDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || generatedDate <= new Date(dateToFilter);
      return matchesSearch && matchesStatus && matchesGeneratedBy && matchesDateFrom && matchesDateTo;
    });
  }, [generations, letters, searchTerm, statusFilter, createdByFilter, dateFromFilter, dateToFilter]);

  const buildTemplateExportData = () => filteredLetters.map(letter => ({
    'Template Name': letter.templateName, 'Subject': letter.subject,
    'Type': letter.isDefault ? 'Default' : 'Custom', 'Created By': letter.createdBy,
    'Created Date': new Date(letter.createdAt).toLocaleDateString(),
    'Updated Date': new Date(letter.updatedAt).toLocaleDateString(),
    'Content Preview': letter.content.substring(0, 100) + (letter.content.length > 100 ? '...' : '')
  }));
  const handleExportTemplates = () => exportToExcel(buildTemplateExportData(), `car_letter_templates_${new Date().toISOString().split('T')[0]}`);
  const handlePrintTemplates = () => printAsExcel(buildTemplateExportData(), 'Car Letter Templates');

  const buildGenerationExportData = () => filteredGenerations.map(generation => {
    const letter = letters.find(l => l.id === generation.letterId);
    const carCodes = generation.selectedItems.map(id => { const car = cars.find(c => c.id === id); return car?.code || 'Unknown'; });
    return {
      'Template Name': letter?.templateName || 'Unknown', 'Selected Cars': carCodes.join(', '),
      'Generated Date': new Date(generation.generatedDate).toLocaleDateString(),
      'Generated By': generation.generatedBy,
      'Status': generation.status.charAt(0).toUpperCase() + generation.status.slice(1),
      'Recipients': generation.recipients?.join(', ') || '', 'Notes': generation.notes || ''
    };
  });
  const handleExportGenerations = () => exportToExcel(buildGenerationExportData(), `car_letter_generations_${new Date().toISOString().split('T')[0]}`);
  const handlePrintGenerations = () => printAsExcel(buildGenerationExportData(), 'Car Letter Generations');

  const letterColumns: TableColumn[] = [
    { key: 'templateName', label: 'Template Name', sortable: true },
    { key: 'subject', label: 'Subject', sortable: true },
    { key: 'isDefault', label: 'Type', sortable: true,
      render: (value: boolean) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
          {value ? 'Default' : 'Custom'}
        </span>
      ) },
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() }
  ];

  const generationColumns: TableColumn[] = [
    { key: 'letterId', label: 'Template', sortable: true,
      render: (value: string) => { const letter = letters.find(l => l.id === value); return letter?.templateName || 'Unknown'; } },
    { key: 'selectedItems', label: 'Cars', sortable: false,
      render: (value: string[]) => {
        const carCodes = value.map(id => { const car = cars.find(c => c.id === id); return car?.code || 'Unknown'; }).join(', ');
        return carCodes.length > 50 ? `${carCodes.substring(0, 50)}...` : carCodes;
      } },
    { key: 'generatedDate', label: 'Generated', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'generatedBy', label: 'Generated By', sortable: true },
    { key: 'status', label: 'Status', sortable: true,
      render: (value: string) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'sent' ? 'bg-green-100 text-green-800' : value === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
        }`}>{value.charAt(0).toUpperCase() + value.slice(1)}</span>
      ) }
  ];

  const handleEditLetter = (letter: PermitLetter) => { setSelectedLetter(letter); setIsFormOpen(true); };
  const handleDeleteLetter = async (letter: PermitLetter) => {
    if (window.confirm(`Are you sure you want to delete the template "${letter.templateName}"?`)) {
      try { await deleteMutation.execute(letter); } catch { /* Error handled by hook toast */ }
    }
  };
  const handleAddLetter = () => { setSelectedLetter(undefined); setIsFormOpen(true); };
  const handleGenerateLetter = () => { setIsGeneratorOpen(true); };

  const handleSubmitLetter = async (data: Partial<PermitLetter>) => {
    try {
      if (selectedLetter) { await updateMutation.execute({ id: selectedLetter.id, data: { ...data, type: 'car' } }); }
      else { await createMutation.execute({ ...data, type: 'car' }); }
    } catch { /* Error handled by hook toast */ }
    setIsFormOpen(false); setSelectedLetter(undefined);
  };

  const handleSubmitGeneration = (data: Partial<LetterGeneration>) => {
    const newGeneration: LetterGeneration = {
      id: Date.now().toString(), letterId: data.letterId || '', letterType: 'car',
      selectedItems: data.selectedItems || [], generatedDate: new Date().toISOString(),
      generatedBy: 'admin', status: 'draft', customContent: data.customContent,
      recipients: data.recipients, notes: data.notes
    };
    setGenerations([...generations, newGeneration]);
    setIsGeneratorOpen(false);
  };

  const handleCancel = () => { setIsFormOpen(false); setIsGeneratorOpen(false); setSelectedLetter(undefined); };

  const clearFilters = () => {
    setSearchTerm(''); setTypeFilter('all'); setStatusFilter('all');
    setCreatedByFilter('all'); setDateFromFilter(''); setDateToFilter('');
  };

  const templateFilters: FilterConfig[] = [
    { key: 'type', label: 'Type', type: 'select', value: typeFilter, onChange: setTypeFilter,
      options: [{ value: 'all', label: 'All Types' }, { value: 'default', label: 'Default' }, { value: 'custom', label: 'Custom' }] },
    { key: 'createdBy', label: 'Created By', type: 'select', value: createdByFilter, onChange: setCreatedByFilter,
      options: [{ value: 'all', label: 'All Users' }, ...uniqueCreatedBy.map(u => ({ value: u, label: u }))] },
    { key: 'dateFrom', label: 'Created From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Created To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const generationFilters: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter,
      options: [{ value: 'all', label: 'All Statuses' }, { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'pending', label: 'Pending' }] },
    { key: 'generatedBy', label: 'Generated By', type: 'select', value: createdByFilter, onChange: setCreatedByFilter,
      options: [{ value: 'all', label: 'All Users' }, ...uniqueGeneratedBy.map(u => ({ value: u, label: u }))] },
    { key: 'dateFrom', label: 'Generated From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Generated To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) return <ErrorState entityName="car letters" message={error.message} onRetry={refetch} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Car Letters Management"
        description="Manage letter templates and generate letters for multiple vehicles"
        actions={[{ label: 'Generate Letter', onClick: handleGenerateLetter, variant: 'primary' }]}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'templates' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            Letter Templates
          </button>
          <button onClick={() => setActiveTab('generations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'generations' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            Generated Letters
          </button>
        </nav>
      </div>

      {activeTab === 'templates' ? (
        <>
          <FilterBar
            searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search templates..."
            filters={templateFilters} onClearFilters={clearFilters}
            totalCount={letters.length} filteredCount={filteredLetters.length} entityName="Letter Templates"
            onAdd={handleAddLetter} addLabel="Add Template" onExport={handleExportTemplates} onPrint={handlePrintTemplates}
          />
          <div className="card">
            <Table columns={letterColumns} data={filteredLetters} onEdit={handleEditLetter} onDelete={handleDeleteLetter} showAddButton={false} searchable={false} />
          </div>
        </>
      ) : (
        <>
          <FilterBar
            searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search generated letters..."
            filters={generationFilters} onClearFilters={clearFilters}
            totalCount={generations.length} filteredCount={filteredGenerations.length} entityName="Generated Letters"
            onExport={handleExportGenerations} onPrint={handlePrintGenerations}
          />
          <div className="card">
            <Table columns={generationColumns} data={filteredGenerations} showAddButton={false} showEditButton={false} showDeleteButton={false} searchable={false} />
          </div>
        </>
      )}

      {isFormOpen && <CarLetterForm letter={selectedLetter} onSubmit={handleSubmitLetter} onCancel={handleCancel} />}
      {isGeneratorOpen && <CarLetterGenerator letters={letters} cars={cars} onSubmit={handleSubmitGeneration} onCancel={handleCancel} />}
    </div>
  );
};

export default CarLetters;
